import { StandardEvents } from '@shopify/events';

const OPEN_DELAY = 90;
const CLOSE_DELAY = 160;
const DESKTOP = window.matchMedia('(min-width: 990px)');

/**
 * Politics Jeans header.
 * - Desktop mega menu with hover intent, focus support and Escape to close.
 * - Mobile menu drawer (native <dialog>).
 * - Transparent → solid on scroll, hover or open menu.
 * - Keeps --header-height in sync for Horizon components that read it.
 */
class PjHeader extends HTMLElement {
  /** @type {HTMLElement | null} */
  #openItem = null;
  #openTimer = 0;
  #closeTimer = 0;
  #abort = new AbortController();
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    const { signal } = this.#abort;
    const items = this.querySelectorAll('[data-pj-mega-item]');

    for (const item of items) {
      const el = /** @type {HTMLElement} */ (item);
      el.addEventListener('pointerenter', (e) => this.#onEnter(el, e), { signal });
      el.addEventListener('pointerleave', (e) => this.#onLeave(e), { signal });
      el.addEventListener('focusin', () => this.#open(el), { signal });
    }

    this.addEventListener(
      'focusout',
      (e) => {
        const next = /** @type {Node | null} */ (e.relatedTarget);
        if (!next || !this.#openItem?.contains(next)) this.#close();
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape' || !this.#openItem) return;
        const link = /** @type {HTMLElement | null} */ (this.#openItem.querySelector('.pj-nav__link'));
        this.#close(true);
        link?.focus();
      },
      { signal }
    );

    // Mobile drawer
    const drawer = /** @type {HTMLDialogElement | null} */ (this.querySelector('#PjMenuDrawer'));
    const opener = this.querySelector('[data-pj-menu-open]');
    opener?.addEventListener(
      'click',
      () => {
        drawer?.showModal();
        opener.setAttribute('aria-expanded', 'true');
        document.documentElement.style.overflow = 'hidden';
      },
      { signal }
    );
    this.querySelector('[data-pj-menu-close]')?.addEventListener('click', () => drawer?.close(), { signal });
    drawer?.addEventListener(
      'close',
      () => {
        opener?.setAttribute('aria-expanded', 'false');
        document.documentElement.style.overflow = '';
      },
      { signal }
    );
    drawer?.addEventListener(
      'click',
      (e) => {
        if (e.target === drawer) drawer.close(); // backdrop click
      },
      { signal }
    );
    DESKTOP.addEventListener('change', () => DESKTOP.matches && drawer?.open && drawer.close(), { signal });

    // Transparent header
    if (this.hasAttribute('data-transparent')) {
      const update = () => this.#updateSolid();
      // Horizon scrolls .page-wrapper on desktop and the document on mobile; capture both.
      document.addEventListener('scroll', update, { passive: true, capture: true, signal });
      this.addEventListener('pointerenter', update, { signal });
      this.addEventListener('pointerleave', update, { signal });
      update();
    }

    // Header height for Horizon components (drawers, sticky elements)
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const height = `${Math.round(entry.borderBoxSize?.[0]?.blockSize ?? this.offsetHeight)}px`;
      // --pj-sticky-top drives pj-* sticky elements; --header-height is for Horizon components.
      document.documentElement.style.setProperty('--pj-sticky-top', height);
      document.body.style.setProperty('--header-height', height);
      document.body.style.setProperty('--header-group-height', height);
    });
    this.#resizeObserver.observe(this.querySelector('.pj-header__bar') ?? this);
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
  }

  /**
   * @param {HTMLElement} item
   * @param {PointerEvent} event
   */
  #onEnter(item, event) {
    if (event.pointerType === 'touch' || !DESKTOP.matches) return;
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#openTimer);
    // Switch instantly when moving between items; delay only the first open.
    const delay = this.#openItem ? 0 : OPEN_DELAY;
    this.#openTimer = window.setTimeout(() => this.#open(item), delay);
  }

  /** @param {PointerEvent} event */
  #onLeave(event) {
    if (event.pointerType === 'touch') return;
    clearTimeout(this.#openTimer);
    this.#closeTimer = window.setTimeout(() => this.#close(), CLOSE_DELAY);
  }

  /** @param {HTMLElement} item */
  #open(item) {
    clearTimeout(this.#closeTimer);
    if (this.#openItem === item) return;
    this.#close();
    this.#openItem = item;
    item.setAttribute('data-open', '');
    item.querySelector('.pj-nav__link')?.setAttribute('aria-expanded', 'true');
    this.setAttribute('data-mega-open', '');
    this.#updateSolid();
  }

  #close(immediate = false) {
    const item = this.#openItem;
    if (!item) return;
    item.removeAttribute('data-open');
    item.querySelector('.pj-nav__link')?.setAttribute('aria-expanded', 'false');
    this.#openItem = null;
    this.removeAttribute('data-mega-open');
    if (immediate) clearTimeout(this.#openTimer);
    this.#updateSolid();
  }

  #updateSolid() {
    if (!this.hasAttribute('data-transparent')) return;
    const scrolled = Math.max(window.scrollY, document.querySelector('.page-wrapper')?.scrollTop ?? 0);
    const solid = scrolled > 8 || this.matches(':hover') || Boolean(this.#openItem);
    this.toggleAttribute('data-solid', solid);
  }
}

/** Live cart count. Updates from the standard cart:lines-update event. */
class PjCartCount extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      async (event) => {
        try {
          const result = await /** @type {any} */ (event).promise;
          const qty = result?.cart?.totalQuantity;
          if (typeof qty === 'number') this.textContent = String(qty);
        } catch {
          /* add failed; count unchanged */
        }
      },
      { signal: this.#abort.signal }
    );

    // bfcache restore: refresh the count
    window.addEventListener(
      'pageshow',
      async (e) => {
        if (!e.persisted) return;
        const res = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`);
        const cart = await res.json();
        this.textContent = String(cart.item_count);
      },
      { signal: this.#abort.signal }
    );
  }

  disconnectedCallback() {
    this.#abort.abort();
  }
}

if (!customElements.get('pj-header')) customElements.define('pj-header', PjHeader);
if (!customElements.get('pj-cart-count')) customElements.define('pj-cart-count', PjCartCount);
